/* Mission across every company: each brain/<company>/plan/bets.md bundled at build time (a deploy picks up edits;
   a new company shows up once its bets.md exists). Every row carries `company`, so the company filter narrows it
   (the VidMax view shows only VidMax's goal and active bets). */
import { activeBets, missionSources } from "../betsModel"
import type { IMissionSource } from "../betsModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { betDetails, staticState } from "./vidmaxPlanWidgets"
import { KeyValues, REPO_BLOB } from "./WidgetParts"

const SOURCES = missionSources(
  import.meta.glob<string>("../../../../../../brain/*/plan/bets.md", { query: "?raw", import: "default", eager: true }),
)

/** Display names where the slug's casing is wrong; everything else is title-cased */
const COMPANY_NAMES: Record<string, string> = {
  aixinternal: "AIX",
  humantext: "HumanText",
  recoverme: "RecoverMe",
  "saas-engine": "SaaS Engine",
  vidmax: "VidMax",
}
const companyName = (slug: string) => COMPANY_NAMES[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1)

const goalText = (source: IMissionSource, label: string) =>
  source.summary.goal.find((item) => item.label.toLowerCase() === label.toLowerCase())?.text

const companyRows = (source: IMissionSource): IWidgetRow[] => {
  const { company, summary } = source
  const href = `${REPO_BLOB}/${source.path}`
  const name = companyName(company)
  const active = activeBets(summary)
  const goal: IWidgetRow = {
    key: `${company}:goal`,
    company,
    verdict: active.length ? EVerdict.WATCH : EVerdict.UNKNOWN,
    pill: { text: name, verdict: EVerdict.UNKNOWN },
    title: goalText(source, "Goal") ?? summary.goal[0]?.text ?? "No goal set",
    sub: goalText(source, "Focus"),
    // Success stays in details: meta never wraps, so a sentence there squeezes the goal title to nothing
    details: <KeyValues pairs={summary.goal.map((item) => [item.label, item.text])} />,
    href,
    hrefLabel: "Open bets.md",
  }
  const bets = active.map(
    (bet): IWidgetRow => ({
      key: `${company}:${bet.id}`,
      company,
      verdict: bet.fields["Next action"] ? EVerdict.ACT : EVerdict.WATCH,
      pill: { text: bet.id, verdict: EVerdict.ACT },
      title: bet.title,
      sub: bet.fields["Next action"] ? `Next: ${bet.fields["Next action"]}` : bet.fields.Why,
      meta: bet.fields.Impact,
      details: betDetails(bet),
      href,
      hrefLabel: "Open bets.md",
    }),
  )
  const timeline = summary.timeline.map(
    (item): IWidgetRow => ({
      key: `${company}:t:${item.label}`,
      company,
      verdict: EVerdict.CLEAR,
      quiet: true,
      title: `${name} · ${item.label}`,
      sub: item.text,
    }),
  )
  return [goal, ...bets, ...timeline]
}

const MISSION_RESULT: IWidgetResult = (() => {
  const rows = SOURCES.flatMap(companyRows)
  const active = rows.filter((row) => row.verdict === EVerdict.ACT).length
  return {
    verdict: active ? EVerdict.ACT : EVerdict.UNKNOWN,
    count: SOURCES.length === 1 ? `${active} active` : `${SOURCES.length} companies · ${active} active`,
    rows,
    ordered: true,
    quietLabel: "Timeline",
    empty: "No brain/<company>/plan/bets.md yet.",
    foot: "From brain/<company>/plan/bets.md",
  }
})()

export const missionWidget: IWidgetDefinition = {
  id: "mission",
  title: "Mission",
  description: "Each company's goal, active bets and their next action",
  columns: { 2: 1, 3: 1, 4: 1, 5: 1, 6: 1 },
  useWidget: () => staticState(MISSION_RESULT),
}
